import { KeyboardEventHandler, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { ColumnMeta, Row, RowData, Table } from '@tanstack/react-table';

import { logError } from 'services/logging/logging';
import { isRelativeModificationForWebEDI } from 'services/webedi/modificationGroupHelpers';
import { DisplayModes } from 'services/webedi/types';
import { cn } from 'support/helpers/cn/cn';
import { formatCurrency, getCurrencySymbol } from 'support/helpers/currency/currency';
import { CANONICAL_ANCHOR_TIMEZONE, formatCalendarDay, formatDateToISO } from 'support/helpers/dateTime/dateTime';
import { isLineItemInPlaceholderUnit } from 'support/helpers/packingList/defaultTransportUnit';
import { ColumnTypesInternal, WebEDIRowData } from 'support/types/webediTable';
import { useDocumentCurrency, useEnumerableOptions } from 'components/WebEDI/helpers';

import { useIsRowLocked } from '../../contexts/LockedRowsContext';
import { IsReadOnlyParams } from '../../types';

type CellClassNamesProps = {
  row: Pick<Row<RowData>, 'index'>;
  table: Pick<Table<RowData>, 'getRowCount'>;
  isFirstColumn: boolean;
  isLastColumn: boolean;
};

export const useCellClassNames = ({ row, table, isFirstColumn, isLastColumn }: CellClassNamesProps): string => {
  const totalRows = table.getRowCount();
  return useMemo(() => {
    const isLastRow = totalRows - 1 === row.index;

    return cn({
      'rounded-bl-md': isFirstColumn && isLastRow,
      'rounded-br-md': isLastColumn && isLastRow,
    });
  }, [totalRows, row.index, isFirstColumn, isLastColumn]);
};

type CellConfigurationProps = {
  meta: ColumnMeta<RowData, unknown> | undefined;
  value: string | number | boolean | undefined;
  mode: DisplayModes;
  fieldId: string;
  rowData: RowData;
};

type CellConfigurationResult = {
  prefix: string;
  suffix: string;
  formattedValue: string | undefined;
};

export const useEnumerableConfigurations = ({
  meta,
  value,
}: Pick<CellConfigurationProps, 'meta' | 'value'>): Pick<CellConfigurationResult, 'formattedValue'> => {
  const { options } = useEnumerableOptions(meta?.optionalConfig);
  const formattedValue = options?.find((option) => option.value === value)?.label ?? undefined;

  return { formattedValue };
};

export const useDateConfigurations = ({
  value,
  mode,
}: Pick<CellConfigurationProps, 'value' | 'mode'>): Pick<CellConfigurationResult, 'formattedValue'> => {
  const formattedValue = formatDateValue(value, mode);

  return { formattedValue };
};

export const useMonetaryConfigurations = ({
  value,
  mode,
  fieldId,
  rowData,
  currencyFieldPath,
}: Omit<CellConfigurationProps, 'meta'> & {
  rowData: WebEDIRowData;
  currencyFieldPath?: string;
}): CellConfigurationResult => {
  //The isPercentage is only needed because of the relative modifications. We use the same cell, just change the configurations
  const isPercentage =
    isModificationAmount(fieldId) &&
    isRelativeModificationForWebEDI(fieldId, rowData, mode, Boolean(fieldId?.includes('line_items')));
  const currency = useDocumentCurrency({ name: currencyFieldPath });

  const { formattedValue, prefix } = isPercentage
    ? { formattedValue: value as string, prefix: '' }
    : formatMonetaryField(value, currency, mode);

  return { prefix: isPercentage ? '' : prefix, suffix: isPercentage ? '%' : '', formattedValue };
};

export const useNumberConfigurations = ({
  meta,
  value,
  mode,
  fieldId,
  rowData,
}: CellConfigurationProps & { rowData: WebEDIRowData }): CellConfigurationResult => {
  const isPercentage =
    isModificationAmount(fieldId) &&
    isRelativeModificationForWebEDI(fieldId, rowData, mode, Boolean(fieldId?.includes('line_items')));
  const { formattedValue, prefix, suffix } = formatNumberField(value, meta, isPercentage, mode);

  return { prefix, suffix, formattedValue };
};

export const formatMonetaryField = (value: unknown, currency: string, mode: DisplayModes) => {
  let formattedValue = '';
  let prefix = '';

  try {
    if (mode === DisplayModes.create || mode === DisplayModes.edit) {
      prefix = getCurrencySymbol(currency);
    }
    formattedValue = value ? formatCurrency(parseFloat(value as string), currency) : '';
  } catch (e) {
    logError(e);
    prefix = '';
  }

  return { formattedValue, prefix };
};

export const formatNumberField = (
  value: CellConfigurationProps['value'],
  meta: CellConfigurationProps['meta'],
  isPercentage: boolean,
  mode: DisplayModes,
): CellConfigurationResult => {
  const prefix = isPercentage ? '' : (meta?.optionalConfig?.prefix ?? '');
  const suffix = isPercentage ? '%' : (meta?.optionalConfig?.suffix ?? '');
  let formattedValue = typeof value === 'number' && isNaN(value) ? undefined : value ? String(value) : '';

  if (mode === DisplayModes.view) {
    formattedValue = formattedValue == undefined ? '' : formattedValue;
  }

  return { formattedValue, prefix, suffix };
};

export const isProcurosAddedValue = (row: Row<WebEDIRowData>): boolean => {
  return Boolean(row.original?._internal?.[ColumnTypesInternal.procuros_added]);
};

export const getCellNestedLevel = (row: Row<WebEDIRowData>): number => {
  return row.original?._internal?.level ?? 0;
};

/**
 * Builds a column's `meta.isReadOnly` resolver. `forcedReadOnly` (e.g. packing-list-disabled) is honored in
 * every mode/row since it isn't a per-row concept. `forcedReadOnlyUnlessError` locks the same way, except a
 * data cell with a datacheck error stays editable so a bad value can still be fixed; the header cell has no
 * error, so it always reads as locked (no bulk edit). Either forced lock beats `isAdded`. `isReadOnly`
 * (process-spec `is_readonly`) is honored for the header cell's static lock icon/bulk-edit eligibility, and
 * for data cells in view and create mode — but never in edit mode, and never for user-added or errored rows,
 * which stay editable.
 */
export const createIsReadOnly = (isReadOnly: boolean, forcedReadOnly = false, forcedReadOnlyUnlessError = false) => {
  return (params?: IsReadOnlyParams): boolean => {
    if (forcedReadOnly) {
      return true;
    }
    if (forcedReadOnlyUnlessError && !params?.hasError) {
      return true;
    }
    if (params?.isHeaderCell) {
      return isReadOnly;
    }
    if (params?.isAdded || params?.hasError) {
      return false;
    }
    return isReadOnly && (params?.mode === DisplayModes.view || params?.mode === DisplayModes.create);
  };
};

type IsReadonlyProps = {
  columnMeta: ColumnMeta<RowData, unknown> | undefined;
  isAdded: boolean;
  mode: DisplayModes;
  rowIndex: number;
  fieldId: string;
  hasError: boolean;
};
const HARCODED_READONLY_FIELDS = [
  //parties.*.type
  new RegExp('^parties\\.\\d+\\.type$'),
];
export const useIsReadonly = ({ columnMeta, isAdded, mode, rowIndex, fieldId, hasError }: IsReadonlyProps) => {
  // A row its table owner has locked (see `LockedRowsContext`) is read-only in every mode, whether
  // or not the user added it and whatever the column says.
  const isRowLocked = useIsRowLocked(rowIndex);
  if (isRowLocked) {
    return true;
  }
  // Columns can force read-only regardless of mode/row (e.g. disabled while packing list is enabled) by
  // returning true here; everything else falls through to the mode/row exceptions below.
  if (columnMeta?.isReadOnly?.({ mode, isAdded, rowIndex, fieldId, hasError })) {
    return true;
  }
  // if the row is added by the user, the field is always editable
  if (isAdded || hasError) {
    return false;
  }
  if (mode === DisplayModes.edit) {
    return HARCODED_READONLY_FIELDS.some((field) => fieldId.match(field));
  }
  if (columnMeta?.optionalConfig?.expression) {
    return true;
  }
  return Boolean(columnMeta?.rowConfig?.[rowIndex]?.is_readonly);
};

export const formatDateValue = (value: unknown, mode: DisplayModes) => {
  let date;

  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string' || typeof value === 'number') {
    const timestamp = typeof value === 'string' ? parseInt(value) : value;
    date = new Date(timestamp * 1000);
  }

  if (date && !isNaN(date.getTime())) {
    const isoDate = formatDateToISO(date, CANONICAL_ANCHOR_TIMEZONE);
    return mode === DisplayModes.create || mode === DisplayModes.edit ? isoDate : formatCalendarDay(isoDate);
  }

  return undefined;
};

export const useLineItemsTransportUnitsConfig = (
  fieldId: string,
  rowData: WebEDIRowData,
  packingListEnabled = false,
) => {
  const isItemTransportUnit = fieldId.endsWith('transport_unit') && fieldId.includes('line_items');
  const shouldWatchTransportUnits = packingListEnabled && isItemTransportUnit;

  const transportUnits = useWatch({ name: 'transport_units', disabled: !shouldWatchTransportUnits });

  // An empty/blank quantity now reads as not shipped (a fresh item starts unshipped until the
  // user enters a quantity); NaN/undefined coerce to 0.
  const shippedQuantity = Number((rowData as { shipped_quantity?: unknown })?.shipped_quantity ?? 0);
  const isNotShippedByQuantity = !(shippedQuantity > 0);

  // With the packing list enabled, an item that ships but sits in a placeholder (SSCC-less) unit —
  // or is still unplaced — reads "unassigned" (that unit is treated as "no transport unit yet"),
  // as opposed to a zero-quantity item, which reads "not shipped".
  const isUnassignedByPlaceholder =
    shouldWatchTransportUnits &&
    !isNotShippedByQuantity &&
    isLineItemInPlaceholderUnit(rowData, (transportUnits as Array<WebEDIRowData>) ?? []);

  const isReadOnly = isItemTransportUnit && (isNotShippedByQuantity || isUnassignedByPlaceholder);

  return { isReadOnly, isUnassigned: isReadOnly && isUnassignedByPlaceholder };
};

export const disableArrowKeys: KeyboardEventHandler<HTMLInputElement> = (e) => {
  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
    e.preventDefault();
  }
};

/**
 * When a main line item's transport unit changes, cascade the new value to all
 * of its sub-items (and their sub-items, recursively).
 *
 * The datachecks library stores `_internal.parent_line_item` as a JSONPath string
 * (e.g. `$['line_items'][0]`) pointing to the parent row. The flat form path for
 * row N is `line_items.N._internal.parent_transport_unit`.
 *
 * @param fieldId  - fieldId of the changed TU cell, e.g. `line_items.3._internal.parent_transport_unit`
 * @param newValue - the new transport unit value
 * @param allItems - the full flat line-items array from RHF `getValues(prefix)` — always current
 * @param setValue - setValue from DataTableContext
 */
export const cascadeTransportUnitToChildren = (
  fieldId: string,
  newValue: string,
  allItems: Array<WebEDIRowData>,
  setValue: (name: string | undefined, value: unknown, options?: Record<string, boolean>) => void,
): void => {
  // fieldId format: "<prefix>.<rowIndex>._internal.parent_transport_unit"
  const match = fieldId.match(/^(.+)\.(\d+)\._internal\.parent_transport_unit$/);
  if (!match) {
    return;
  }

  const [, prefix, rowIndexStr] = match;
  const rowIndex = parseInt(rowIndexStr, 10);
  // The datachecks library (via JSONPath.toPathString) formats parent_line_item as `$['<prefix>'][N]`
  const parentLinePath = `$['${prefix}'][${rowIndex}]`;

  allItems.forEach((item, index) => {
    if (item?._internal?.parent_line_item !== parentLinePath) {
      return;
    }

    const childFieldId = `${prefix}.${index}._internal.parent_transport_unit`;
    setValue(childFieldId, newValue, { shouldDirty: true });

    // Recurse for grandchildren
    cascadeTransportUnitToChildren(childFieldId, newValue, allItems, setValue);
  });
};

const isModificationAmount = (fieldId: string | undefined): boolean => {
  if (!fieldId) {
    return false;
  }
  return (
    fieldId.includes('modification_groups') &&
    (fieldId.includes('allowances') || fieldId.includes('charges')) &&
    (fieldId.includes('percentage') || fieldId.includes('amount'))
  );
};
